import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../ui/Icon/Icon';
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES, type AppLanguage } from '../../../i18n/config';
import { useLanguage } from '../../../i18n/useLanguage';
import styles from './LanguageSelect.module.css';

type MenuPlacement = 'below' | 'above';

interface LanguageSelectProps {
    placement?: MenuPlacement;
}

const NAVIGATION_KEYS: Record<string, (index: number, count: number) => number> = {
    ArrowDown: (index, count) => (index + 1) % count,
    ArrowUp: (index, count) => (index - 1 + count) % count,
    Home: () => 0,
    End: (_, count) => count - 1
};

export default function LanguageSelect({ placement = 'below' }: LanguageSelectProps) {
    const { t } = useTranslation();
    const { language, setLanguage } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const menuId = useId();

    const close = (returnFocus: boolean) => {
        setIsOpen(false);
        if (returnFocus) triggerRef.current?.focus();
    };

    useEffect(() => {
        if (!isOpen) return;
        optionRefs.current[SUPPORTED_LANGUAGES.indexOf(language)]?.focus();

        const closeOnOutsidePointer = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener('pointerdown', closeOnOutsidePointer);
        return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
    }, [isOpen, language]);

    const choose = (code: AppLanguage) => {
        close(true);
        if (code !== language) setLanguage(code);
    };

    const handleMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
            return;
        }
        if (event.key === 'Tab') {
            close(false);
            return;
        }
        const nextIndex = NAVIGATION_KEYS[event.key];
        if (!nextIndex) return;
        event.preventDefault();
        const current = optionRefs.current.findIndex((option) => option === document.activeElement);
        optionRefs.current[nextIndex(Math.max(current, 0), SUPPORTED_LANGUAGES.length)]?.focus();
    };

    return (
        <div ref={rootRef} className={styles.root} data-placement={placement} data-open={isOpen}>
            <button
                ref={triggerRef}
                type="button"
                className={styles.trigger}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                onClick={() => setIsOpen((open) => !open)}
            >
                <Icon name="globe" className={styles.icon} />
                <span className={styles.code} aria-hidden="true">{language.toUpperCase()}</span>
                <span className="visually-hidden">{t('language.label')}: {LANGUAGE_NAMES[language]}</span>
                <Icon name="chevronDown" className={styles.chevron} />
            </button>
            {isOpen && (
                <ul id={menuId} role="menu" aria-label={t('language.label')} className={styles.menu} onKeyDown={handleMenuKeyDown}>
                    {SUPPORTED_LANGUAGES.map((code, index) => (
                        <li key={code} role="none">
                            <button
                                ref={(element) => {
                                    optionRefs.current[index] = element;
                                }}
                                type="button"
                                role="menuitemradio"
                                aria-checked={code === language}
                                lang={code}
                                className={styles.option}
                                onClick={() => choose(code)}
                            >
                                <span className={styles.optionCode} aria-hidden="true">{code.toUpperCase()}</span>
                                <span className={styles.optionName}>{LANGUAGE_NAMES[code]}</span>
                                <Icon name="check" className={styles.check} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
